import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Briefcase, Star, MousePointerClick, Users, MessageSquare, AlertTriangle,
    RefreshCw, ArrowUp, ArrowDown,
} from 'lucide-react';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import StatsKpiCard from './StatsKpiCard';
import StatsCard from './StatsCard';
import {
    getOverview, getProvidersTimeline, getReviewsTimeline, getUsersTimeline,
    getTopCategories, getTopProvidersByEngagement, getCatalogHealth,
} from '../../../services/statsApi';
import './Estadisticas.css';

const RANGE_OPTIONS = [
    { value: '7d', label: '7 días' },
    { value: '30d', label: '30 días' },
    { value: '90d', label: '90 días' },
    { value: '365d', label: '1 año' },
];

const formatNumber = (n) => {
    if (n == null) return '—';
    return new Intl.NumberFormat('es-AR').format(n);
};

const groupByForRange = (range) => {
    if (range === '7d') return 'day';
    if (range === '365d') return 'month';
    return 'week';
};

const Estadisticas = () => {
    const [range, setRange] = useState('30d');

    const [overview, setOverview] = useState(null);
    const [providersTl, setProvidersTl] = useState(null);
    const [reviewsTl, setReviewsTl] = useState(null);
    const [usersTl, setUsersTl] = useState(null);
    const [topCategories, setTopCategories] = useState(null);
    const [topProviders, setTopProviders] = useState(null);
    const [health, setHealth] = useState(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async (selectedRange) => {
        const groupBy = groupByForRange(selectedRange);
        try {
            const results = await Promise.allSettled([
                getOverview(selectedRange),
                getProvidersTimeline(selectedRange, groupBy),
                getReviewsTimeline(selectedRange, groupBy),
                getUsersTimeline(selectedRange, groupBy),
                getTopCategories(10),
                getTopProvidersByEngagement(10),
                getCatalogHealth(),
            ]);
            const [ov, pTl, rTl, uTl, cats, provs, h] = results;
            setOverview(ov.status === 'fulfilled' ? ov.value : null);
            setProvidersTl(pTl.status === 'fulfilled' ? pTl.value : null);
            setReviewsTl(rTl.status === 'fulfilled' ? rTl.value : null);
            setUsersTl(uTl.status === 'fulfilled' ? uTl.value : null);
            setTopCategories(cats.status === 'fulfilled' ? cats.value : null);
            setTopProviders(provs.status === 'fulfilled' ? provs.value : null);
            setHealth(h.status === 'fulfilled' ? h.value : null);
            const anyFailed = results.some((r) => r.status === 'rejected');
            setError(anyFailed ? 'Algunas métricas no se pudieron cargar.' : null);
        } catch (e) {
            console.error('Error cargando estadísticas:', e);
            setError('No se pudieron cargar las estadísticas.');
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            await fetchAll(range);
            if (mounted) setLoading(false);
        };
        load();
        return () => { mounted = false; };
    }, [range, fetchAll]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchAll(range);
        setRefreshing(false);
    };

    const providerKpi = overview?.providers;
    const reviewsKpi = overview?.reviews;
    const usersKpi = overview?.users;
    const engagementKpi = overview?.engagement;
    const forumKpi = overview?.forum;

    const ratingDist = useMemo(() => {
        const r = health?.ratingDistribution;
        if (!r) return [];
        return [
            { name: 'Sin rating', value: r.noRating || 0, color: 'var(--admin-text-subtle)' },
            { name: 'Bajo (1-2)', value: r.low || 0, color: 'var(--admin-danger)' },
            { name: 'Medio (3)', value: r.mid || 0, color: 'var(--admin-warning)' },
            { name: 'Alto (4-5)', value: r.high || 0, color: 'var(--admin-success)' },
        ];
    }, [health]);

    const totalActiveProviders = providerKpi
        ? providerKpi.serviceActive + providerKpi.productsActive
        : null;

    return (
        <div className="stats-page">
            <header className="stats-page__header">
                <div>
                    <h1 className="stats-page__title">Estadísticas</h1>
                    <p className="stats-page__subtitle">
                        Indicadores y tendencias del negocio. Período seleccionado: {RANGE_OPTIONS.find(r => r.value === range)?.label}.
                    </p>
                </div>
                <div className="stats-page__controls">
                    <div className="stats-range-toggle" role="tablist" aria-label="Rango temporal">
                        {RANGE_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`stats-range-toggle__btn ${range === opt.value ? 'is-active' : ''}`}
                                onClick={() => setRange(opt.value)}
                                disabled={loading || refreshing}
                                aria-pressed={range === opt.value}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="stats-refresh-btn"
                        onClick={handleRefresh}
                        disabled={refreshing || loading}
                        title="Actualizar"
                    >
                        <RefreshCw size={16} className={refreshing ? 'admin-spin' : ''} />
                    </button>
                </div>
            </header>

            {error && (
                <div className="stats-error" role="alert">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* Fila 1: KPIs */}
            <section className="stats-kpis-grid" aria-label="KPIs principales">
                <StatsKpiCard
                    label="Proveedores activos"
                    value={totalActiveProviders}
                    hint={providerKpi ? `+${formatNumber(providerKpi.newInRange)} altas en el período` : undefined}
                    delta={providerKpi?.newDelta}
                    color="accent"
                    icon={Briefcase}
                    loading={loading}
                />
                <StatsKpiCard
                    label="Rating promedio"
                    value={reviewsKpi?.averageRatingInRange?.toFixed?.(1) ?? '—'}
                    hint={reviewsKpi ? `${formatNumber(reviewsKpi.totalInRange)} reviews en el período` : undefined}
                    delta={reviewsKpi?.delta}
                    color="warning"
                    icon={Star}
                    loading={loading}
                />
                <StatsKpiCard
                    label="Engagement total"
                    value={engagementKpi?.total}
                    hint={engagementKpi ? `${formatNumber(engagementKpi.phoneClicksTotal)} phone · ${formatNumber(engagementKpi.linkClicksTotal)} link` : undefined}
                    color="info"
                    icon={MousePointerClick}
                    loading={loading}
                />
                <StatsKpiCard
                    label="Usuarios nuevos"
                    value={usersKpi?.newInRange}
                    hint={usersKpi ? `${formatNumber(usersKpi.total)} totales` : undefined}
                    delta={usersKpi?.newDelta}
                    color="success"
                    icon={Users}
                    loading={loading}
                />
                <StatsKpiCard
                    label="Posts del foro"
                    value={forumKpi?.newInRange}
                    hint="nuevos en el período"
                    delta={forumKpi?.newDelta}
                    color="info"
                    icon={MessageSquare}
                    loading={loading}
                />
                <StatsKpiCard
                    label="Salud del catálogo"
                    value={health ? `${health.withoutReviewsPct?.toFixed?.(0) ?? 0}%` : null}
                    hint={health ? `${formatNumber(health.withoutReviews)} sin reviews · ${formatNumber(health.expiringSoon)} por vencer` : undefined}
                    color="warning"
                    icon={AlertTriangle}
                    loading={loading}
                />
            </section>

            {/* Fila 2: Timelines */}
            <section className="stats-charts-grid">
                <StatsCard title="Altas de proveedores" subtitle="Servicio vs productos por período">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={providersTl?.series || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                            <XAxis dataKey="bucket" stroke="var(--admin-text-muted)" fontSize={12} />
                            <YAxis stroke="var(--admin-text-muted)" fontSize={12} allowDecimals={false} />
                            <Tooltip
                                contentStyle={tooltipStyle}
                                labelStyle={{ color: 'var(--admin-text)' }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="service" stroke="var(--admin-accent)" strokeWidth={2} dot={false} name="Servicio" />
                            <Line type="monotone" dataKey="products" stroke="var(--admin-info)" strokeWidth={2} dot={false} name="Productos" />
                        </LineChart>
                    </ResponsiveContainer>
                </StatsCard>

                <StatsCard title="Reviews por período" subtitle="Distribución por rating (alto/medio/bajo)">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={reviewsTl?.series || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                            <XAxis dataKey="bucket" stroke="var(--admin-text-muted)" fontSize={12} />
                            <YAxis stroke="var(--admin-text-muted)" fontSize={12} allowDecimals={false} />
                            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'var(--admin-text)' }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="positive" stackId="r" fill="var(--admin-success)" name="≥ 4 estrellas" />
                            <Bar dataKey="neutral"  stackId="r" fill="var(--admin-warning)" name="3 estrellas" />
                            <Bar dataKey="negative" stackId="r" fill="var(--admin-danger)"  name="≤ 2 estrellas" />
                        </BarChart>
                    </ResponsiveContainer>
                </StatsCard>

                <StatsCard title="Crecimiento de usuarios" subtitle="Acumulado en el tiempo">
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={usersTl?.series || []}>
                            <defs>
                                <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--admin-success)" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="var(--admin-success)" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                            <XAxis dataKey="bucket" stroke="var(--admin-text-muted)" fontSize={12} />
                            <YAxis stroke="var(--admin-text-muted)" fontSize={12} allowDecimals={false} />
                            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'var(--admin-text)' }} />
                            <Area type="monotone" dataKey="cumulative" stroke="var(--admin-success)" fill="url(#usersGradient)" name="Usuarios totales" />
                        </AreaChart>
                    </ResponsiveContainer>
                </StatsCard>

                <StatsCard title="Distribución de ratings" subtitle="Proveedores por rango de rating promedio">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={ratingDist} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                            <XAxis type="number" stroke="var(--admin-text-muted)" fontSize={12} allowDecimals={false} />
                            <YAxis dataKey="name" type="category" stroke="var(--admin-text-muted)" fontSize={12} width={100} />
                            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'var(--admin-text)' }} />
                            <Bar dataKey="value" name="Proveedores">
                                {ratingDist.map((entry, idx) => (
                                    <RatingDistCell key={idx} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </StatsCard>
            </section>

            {/* Fila 3: Tops */}
            <section className="stats-tops-grid">
                <StatsCard title="Top categorías" subtitle="Por views totales · delta semanal">
                    <TopCategoriesTable items={topCategories?.items} loading={loading} />
                </StatsCard>

                <StatsCard title="Top proveedores por engagement" subtitle="Phone + link clicks">
                    <TopProvidersTable items={topProviders?.items} loading={loading} />
                </StatsCard>
            </section>
        </div>
    );
};

const tooltipStyle = {
    backgroundColor: 'var(--admin-bg-elevated)',
    border: '1px solid var(--admin-border)',
    borderRadius: 8,
    fontSize: 12,
    color: 'var(--admin-text)',
};

const RatingDistCell = ({ fill }) => <rect fill={fill} />;

const TopCategoriesTable = ({ items, loading }) => {
    if (loading) return <div className="stats-empty">Cargando…</div>;
    if (!items || items.length === 0) return <div className="stats-empty">Sin datos disponibles.</div>;
    return (
        <div className="stats-table-wrapper">
            <table className="stats-table">
                <thead>
                    <tr>
                        <th>Categoría</th>
                        <th>Tipo</th>
                        <th>Views</th>
                        <th>Esta semana</th>
                        <th>Δ semanal</th>
                        <th>Proveedores</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((row) => (
                        <tr key={row.id}>
                            <td>{row.name || '—'}</td>
                            <td><span className={`stats-badge stats-badge--${row.type === 'service' ? 'info' : 'accent'}`}>{row.type}</span></td>
                            <td>{formatNumber(row.views)}</td>
                            <td>{formatNumber(row.currentWeek)}</td>
                            <td><DeltaCell value={row.weekDelta} /></td>
                            <td>{formatNumber(row.providerCount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const TopProvidersTable = ({ items, loading }) => {
    if (loading) return <div className="stats-empty">Cargando…</div>;
    if (!items || items.length === 0) return <div className="stats-empty">Sin datos disponibles.</div>;
    return (
        <div className="stats-table-wrapper">
            <table className="stats-table">
                <thead>
                    <tr>
                        <th>Proveedor</th>
                        <th>Tipo</th>
                        <th>Phone</th>
                        <th>Link</th>
                        <th>Engagement</th>
                        <th>Rating</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((row) => (
                        <tr key={`${row.type}-${row.username}`}>
                            <td>
                                <div className="stats-provider-cell">
                                    <span className="stats-provider-cell__name">{row.name || row.username}</span>
                                    <span className="stats-provider-cell__user">@{row.username}</span>
                                </div>
                            </td>
                            <td><span className={`stats-badge stats-badge--${row.type === 'service' ? 'info' : 'accent'}`}>{row.type}</span></td>
                            <td>{formatNumber(row.phoneClicks)}</td>
                            <td>{formatNumber(row.linkClicks)}</td>
                            <td><strong>{formatNumber(row.engagement)}</strong></td>
                            <td>
                                {row.averageRating != null
                                    ? `${row.averageRating.toFixed(1)} ★ (${row.totalReviews || 0})`
                                    : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const DeltaCell = ({ value }) => {
    if (value == null) return <span className="stats-delta stats-delta--neutral">—</span>;
    if (value > 0) return <span className="stats-delta stats-delta--up"><ArrowUp size={11} />{value.toFixed(1)}%</span>;
    if (value < 0) return <span className="stats-delta stats-delta--down"><ArrowDown size={11} />{Math.abs(value).toFixed(1)}%</span>;
    return <span className="stats-delta stats-delta--neutral">0%</span>;
};

export default Estadisticas;
